import { useMemo } from 'react'

import { useOrderFormStore } from '../stores/order-form.store'
import type { OrderFormParamsLayoutItem } from '../types/order-form-params-layout.type'

interface ExtractedParams {
  isViewOrder: boolean
  isVisibleTransport: boolean
  isEnabledCommission: boolean
  isVisibleCommission: boolean
  hasClientSelected: boolean
  isEnabledShippingType: boolean,
  isEnabledBillingType: boolean,
  isDisabledSecondColumn: boolean
  isActiveFieldSelectSeller: boolean
  isVisibleSignature: boolean
  paramsFieldTransport?: OrderFormParamsLayoutItem
  paramsFieldCommission?: OrderFormParamsLayoutItem
  paramsSelectSeller?: OrderFormParamsLayoutItem
  paramsPaymentConfig?: OrderFormParamsLayoutItem
  autoCalculateEnabled: boolean
  utilizaValorSugerido: boolean
  hidePaymentDeadline: boolean
  getTabVisibility: (tabname: string) => boolean
  hasValidPaymentTerms: boolean
  permiteAlteraDataEmissao: boolean
  descontoItemSobrescreveGeral: boolean
  utilizaTabelaPrecoMobile: boolean
  metaPorQuantidade: boolean
  limitaQuantidadePorEstoque: boolean
}

const findField = (
  params: OrderFormParamsLayoutItem[] | undefined,
  campo: string,
): OrderFormParamsLayoutItem | undefined => {
  return params?.find((item) => item.campo === campo)
}

const getBooleanParam = (value: unknown) => {
  return ['true', '1'].includes(String(value).toLowerCase())
}

const useOrderFormParams = (): ExtractedParams => {
  const { operationType, jsonOrderForm, paramsLayoutOrderForm, paramsOrderForm } =
    useOrderFormStore((state) => ({
      paramsOrderForm: state.paramsOrderForm,
      operationType: state.operationType,
      jsonOrderForm: state.jsonOrderForm,
      paramsLayoutOrderForm: state.paramsLayoutOrderForm,
    }))

  const hasValidPaymentTerms = useMemo(() => {
    if (!jsonOrderForm) return false

    const paymentFields = [
      'condPagto1', 'condPagto2', 'condPagto3', 'condPagto4', 'condPagto5', 'condPagto6',
      'condPagto7', 'condPagto8', 'condPagto9', 'condPagto10', 'condPagto11', 'condPagto12',
      'condPagto13', 'condPagto14', 'condPagto15', 'condPagto16', 'condPagto17', 'condPagto18',
      'condPagto19', 'condPagto20', 'condPagto21', 'condPagto22', 'condPagto23', 'condPagto24'
    ]
    return paymentFields.some(field => {
      const value = jsonOrderForm[field]
      return value !== undefined && value !== null && value !== '' && value.trim() !== ''
    })
  }, [jsonOrderForm])

  const extractedParams = useMemo((): ExtractedParams => {
    const paramsFieldTransport = findField(
      paramsLayoutOrderForm?.dados,
      'fieldTransport',
    )
    const paramsFieldCommission = findField(
      paramsLayoutOrderForm?.dados,
      'commission',
    )

    const paramsFieldShippingType = findField(
      paramsLayoutOrderForm?.dados,
      'shippingType',
    )

    const paramsFieldBillingType = findField(
      paramsLayoutOrderForm?.dados,
      'billingType',
    )

    const paramsSelectSeller = findField(
      paramsLayoutOrderForm?.dados,
      'selectSeller',
    )

    const paramsPrazo = findField(
      paramsLayoutOrderForm?.geral,
      'hidePaymentDeadline',
    )
    const paramsPaymentConfig = paramsLayoutOrderForm?.prazo?.find(
      (item) => item.campo === 'paymentHalfNote',
    )
    const paramsSignature = findField(
      paramsLayoutOrderForm?.finalizar,
      'showSignature',
    )


    function getTabVisibility(tabname: string) {
      const params = findField(
        paramsLayoutOrderForm?.geral,
        `tab${tabname}`,
      )

      if (!params) {
        return true;
      }


      return params?.visible
    }


    const hidePaymentDeadline = paramsPrazo?.visible === false
    const isViewOrder = operationType === 'view'
    const isVisibleTransport = paramsFieldTransport?.visible !== false
    const isEnabledCommission = paramsFieldCommission?.enabled !== false
    const isEnabledBillingType = paramsFieldBillingType?.enabled !== false
    const isEnabledShippingType = paramsFieldShippingType?.enabled !== false
    const isVisibleCommission = paramsFieldCommission?.visible !== false
    const hasClientSelected = jsonOrderForm?.fkCliente !== ''
    const isDisabledSecondColumn = paramsPaymentConfig?.enabled === false
    const isActiveFieldSelectSeller = paramsSelectSeller?.enabled
    const isVisibleSignature = paramsSignature?.visible
    const autoCalculateEnabled = paramsOrderForm?.listaParametroSistema?.find(item => item.campo === 'FVModeloCalculo')?.valor === "1";
    const utilizaValorSugerido = paramsOrderForm?.listaParametroSistema?.find(item => item.campo === 'UtilizaValorSugerido')?.valor === "True";
    const permiteAlteraDataEmissao = paramsOrderForm?.listaParametroSistema?.find(item => item.campo === 'FVAlteraDataEmissao')?.valor !== "False";
    const descontoItemSobrescreveGeral = paramsOrderForm?.listaParametroSistema?.find(item => item.campo === 'FVDescontoItemSobrescreveGeral')?.valor === "True";
    const utilizaTabelaPrecoMobile = jsonOrderForm?.parametroSistemaCalculo?.UtilizaTabelaPrecoMobile === true;
    const metaPorQuantidade = getBooleanParam(
      paramsOrderForm?.listaParametroSistema?.find(
        item => item.campo === 'MetaPorQuantidade',
      )?.valor,
    );
    const reservaPedido = getBooleanParam(
      paramsOrderForm?.listaParametroSistema?.find(
        item => item.campo === 'ReservaPedido',
      )?.valor,
    );
    const limitaQuantidadePorEstoque = reservaPedido &&
      findField(paramsLayoutOrderForm?.itens, 'availableStock')?.visible === true;

    return {
      isViewOrder,
      isVisibleTransport,
      isEnabledCommission,
      isVisibleCommission,
      isVisibleSignature,
      hasClientSelected,
      isEnabledShippingType,
      isEnabledBillingType,
      paramsFieldTransport,
      paramsFieldCommission,
      paramsSelectSeller,
      paramsPaymentConfig,
      isDisabledSecondColumn,
      isActiveFieldSelectSeller,
      utilizaValorSugerido,
      autoCalculateEnabled,
      hidePaymentDeadline,
      getTabVisibility,
      hasValidPaymentTerms,
      permiteAlteraDataEmissao,
      descontoItemSobrescreveGeral,
      utilizaTabelaPrecoMobile,
      metaPorQuantidade,
      limitaQuantidadePorEstoque,
    }
  }, [operationType, jsonOrderForm, paramsLayoutOrderForm, paramsOrderForm, hasValidPaymentTerms])

  return extractedParams
}

export default useOrderFormParams
